import { SOURCE_TYPE_ACH_DEBIT, SOURCE_TYPE_CARD } from '@nucleus/src-giving/constant';
import { FlowRevision } from '@nucleus/src/flows/model/flowrevision';
import { Flow } from '@nucleus/types/flow/flow';
import { deepFreeze } from '../lib/deepFreeze';
import { COLOR_MODE_AUTO, STANDARD_COLOR_DEFAULT } from './color';
import {
  PREFER_SMALLER,
  TRIGGER_ICON_GIVE,
  TRIGGER_POSITION_RIGHT,
  TRIGGER_SHAPE_CIRCLE,
  TRIGGER_SHAPE_PILL,
} from './launcher';

export enum FlowType {
  AccessManagementAccessRequest = 'FLOW_TYPE_ACCESS_MANAGEMENT_ACCESS_REQUEST',
  AccessRequest = 'FLOW_TYPE_ACCESS_REQUEST',
  Giving = 'FLOW_TYPE_GIVING',
  Prayer = 'FLOW_TYPE_PRAYER',
  Custom = 'FLOW_TYPE_CUSTOM',
}

export const FlowTypeAccessRequest = 'FLOW_TYPE_ACCESS_MANAGEMENT_ACCESS_REQUEST';
export const FLOW_TYPE_ACCESS_REQUEST = 'FLOW_TYPE_ACCESS_REQUEST';
export const FLOW_TYPE_GIVING = 'FLOW_TYPE_GIVING';
export const FLOW_TYPE_PRAYER = 'FLOW_TYPE_PRAYER';
export const FLOW_TYPE_CUSTOM = 'FLOW_TYPE_CUSTOM';
export const FLOW_TYPES = deepFreeze(Object.values(FlowType));

export const FLOW_DB_TYPE_GLOBALS = 'globals';
export const FLOW_DB_TYPE_REVISION = 'revision';
export const FLOW_DB_TYPE_SUBSCRIBER = 'subscriber';
export const FLOW_DB_TYPE_URL = 'url';

export const FILTER_SORT_DIRECTION_DESCENDING = 'Desc';
export const FILTER_SORT_DIRECTION_ASCENDING = 'Asc';

export type StepType = keyof typeof STEP_TYPE;

export const STEP_TYPE = deepFreeze({
  ADDITIONAL_CONTACT: 'ADDITIONAL_CONTACT',
  BANK_ACCOUNT: 'BANK_ACCOUNT',
  BIRTHDAY: 'GENERIC_BIRTHDAY',
  CONTACT: 'CONTACT',
  CONTACT_TEXT: 'CONTACT_TEXT',
  CONDITIONAL_PRESTEP: 'CONDITIONAL_PRESTEP',
  CREDIT_CARD: 'CREDIT_CARD',
  DATETIME: 'DATETIME',
  EMAIL_ADDRESS: 'GENERIC_EMAIL_ADDRESS',
  FILE_UPLOAD: 'FILE_UPLOAD',
  GENDER: 'GENERIC_GENDER',
  GIVING_AMOUNT: 'GIVING_AMOUNT',
  GIVING_DESIGNATION: 'GIVING_DESIGNATION',
  GIVING_SUMMARY: 'GIVING_SUMMARY',
  INFO: 'INFO',
  MULTIPLE_CHOICE: 'MULTIPLE_CHOICE',
  NAME: 'GENERIC_NAME',
  PAYMENT_AMOUNT_MULTIPLE_CHOICE: 'PAYMENT_AMOUNT_MULITPLE_CHOICE',
  PAYMENT_AMOUNT: 'PAYMENT_AMOUNT',
  PAYMENT_INFO: 'PAYMENT_INFO',
  PAYMENT_MEMO: 'PAYMENT_MEMO',
  PAYMENT_SCHEDULE_INTERVAL: 'PAYMENT_SCHEDULE_INTERVAL',
  PAYMENT_SCHEDULE_START_DATE: 'PAYMENT_SCHEDULE_START_DATE',
  PAYMENT_SUMMARY: 'PAYMENT_SUMMARY',
  PAYMENT_TYPE: 'PAYMENT_TYPE',
  PAYMENT: 'PAYMENT',
  PHONE: 'GENERIC_PHONE',
  PHYSICAL_ADDRESS: 'GENERIC_PHYSICAL_ADDRESS',
  PLAID: 'PLAID',
  POST_STEP_TEXT: 'POST_STEP_TEXT',
  PRAYER_PRIVACY: 'PRAYER_PRIVACY',
  PROFILE: 'PROFILE',
  SMART_BIRTHDAY: 'BIRTHDAY',
  SMART_CONTACT: 'SMART_CONTACT',
  SMART_EMAIL_ADDRESS: 'EMAIL_ADDRESS',
  SMART_GENDER: 'SMART_GENDER',
  SMART_NAME: 'NAME',
  SMART_PHONE: 'PHONE',
  SMART_PHYSICAL_ADDRESS: 'PHYSICAL_ADDRESS',
  TEXT: 'TEXT',
} as const);

export const FLOW_SOURCE_TYPES = deepFreeze([SOURCE_TYPE_ACH_DEBIT, SOURCE_TYPE_CARD] as const);

export const FLOW_STATUS_UNAUTHENTICATED = 'unauthenticated';
export const FLOW_STATUS_UNAUTHORIZED = 'unauthorized';
export const FLOW_STATUS_UNAVAILABLE = 'unavailable';
export const FLOW_STATUS_COMING_SOON = 'coming-soon';
export const FLOW_STATUS_OPEN = 'open';
export const FLOW_STATUS_CLOSED = 'closed';

export const FLOW_STATUS_PAGE_ACTION_TYPE_URL = 'url';
export const FLOW_STATUS_PAGE_ACTION_TYPE_LAUNCHER = 'launcher';
export const FLOW_STATUS_PAGE_ACTION_TYPE_FLOW = 'flow';
export const FLOW_STATUS_PAGE_ACTION_TYPE_CHURCH = 'church';

export const FLOW_STATUS_PAGE_CHURCH_ACTION_HOMEPAGE = 'homepage';
export const FLOW_STATUS_PAGE_CHURCH_ACTION_EMAIL = 'email';

export const FLOW_STATUS_PAGE_ACTION_PRIORITY_PRIMARY = 'primary';
export const FLOW_STATUS_PAGE_ACTION_PRIORITY_SECONDARY = 'secondary';
export const FLOW_STATUS_PAGE_ACTION_PRIORITY_TERTIARY = 'tertiary';

export const FLOW_STATUS_PAGE_CLOSED = 'status_page_closed';
export const FLOW_STATUS_PAGE_COMING_SOON = 'status_page_coming_soon';
export const FLOW_STATUS_PAGE_UNAVAILABLE = 'status_page_unavailable';
export const FLOW_STATUS_PAGE_UNAUTHENTICATED = 'status_page_unauthenticated';
export const FLOW_STATUS_PAGE_UNAUTHORIZED = 'status_page_unauthorized';

export const FLOW_STATUS_PAGES = deepFreeze([
  FLOW_STATUS_PAGE_CLOSED,
  FLOW_STATUS_PAGE_COMING_SOON,
  FLOW_STATUS_PAGE_UNAVAILABLE,
  FLOW_STATUS_PAGE_UNAUTHENTICATED,
  FLOW_STATUS_PAGE_UNAUTHORIZED,
] as const);

export const MAP_FLOW_STATUS_TO_STATUS_PAGE_ATTRIBUTE = deepFreeze({
  [FLOW_STATUS_CLOSED]: FLOW_STATUS_PAGE_CLOSED,
  [FLOW_STATUS_COMING_SOON]: FLOW_STATUS_PAGE_COMING_SOON,
  [FLOW_STATUS_UNAVAILABLE]: FLOW_STATUS_PAGE_UNAVAILABLE,
  [FLOW_STATUS_UNAUTHENTICATED]: FLOW_STATUS_PAGE_UNAUTHENTICATED,
  [FLOW_STATUS_UNAUTHORIZED]: FLOW_STATUS_PAGE_UNAUTHORIZED,
} as const);

const FLOW_STATUS_PAGE_ACTIONS_DEFAULTS = deepFreeze([
  {
    label: 'Go to homepage',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_PRIMARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_CHURCH,
    value: 'homepage',
  },
  {
    label: '',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_SECONDARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_URL,
    value: '',
  },
  {
    label: '',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_TERTIARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_URL,
    value: '',
  },
] as const);

const FLOW_STATUS_PAGE_UNAUTHENTICATED_ACTIONS_DEFAULTS = deepFreeze([
  {
    label: '',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_PRIMARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_CHURCH,
    value: '',
  },
  {
    label: 'or, Go to homepage',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_SECONDARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_CHURCH,
    value: 'homepage',
  },
  {
    label: '',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_TERTIARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_URL,
    value: '',
  },
] as const);

const FLOW_STATUS_PAGE_UNAUTHORIZED_ACTIONS_DEFAULTS = deepFreeze([
  {
    label: 'Go to homepage',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_PRIMARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_CHURCH,
    value: 'homepage',
  },
  {
    label: '',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_SECONDARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_URL,
    value: '',
  },
  {
    label: 'Let us know!',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_TERTIARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_CHURCH,
    value: 'email',
  },
] as const);

export const FLOW_DEFAULTS = deepFreeze<Partial<Flow>>({
  color_flow: STANDARD_COLOR_DEFAULT,
  color_mode: COLOR_MODE_AUTO,
  [FLOW_STATUS_PAGE_CLOSED]: {
    actions: FLOW_STATUS_PAGE_ACTIONS_DEFAULTS,
    body: 'You might have missed the deadline or we’ve reached capacity. Hopefully we’ll be able to get you in next time!',
    headline: 'Sorry! This Flow is now closed :(',
  },
  [FLOW_STATUS_PAGE_COMING_SOON]: {
    actions: FLOW_STATUS_PAGE_ACTIONS_DEFAULTS,
    body: 'It looks like you’re just a little bit too early to complete this particular Flow. But it looks like it will be available soon! In the meantime, feel free to visit our website for all other info.',
    headline: 'Almost, but not yet… This Flow is coming soon!',
  },
  [FLOW_STATUS_PAGE_UNAVAILABLE]: {
    actions: FLOW_STATUS_PAGE_ACTIONS_DEFAULTS,
    body: 'It’s possible you’ve found something that isn’t quite ready for you yet, or you’ve stumbled upon an old link that is no longer available. Either way, we hope you find what you’re looking for.',
    headline: 'Hmm… this Flow isn’t available right now.',
  },
  [FLOW_STATUS_PAGE_UNAUTHENTICATED]: {
    actions: FLOW_STATUS_PAGE_UNAUTHENTICATED_ACTIONS_DEFAULTS,
    body: 'You may also need to have a connection type set with your church (e.g. member, regular attender) depending on how privacy has been set up for this Flow.',
    headline: 'You must sign in to view this content.',
  },
  [FLOW_STATUS_PAGE_UNAUTHORIZED]: {
    actions: FLOW_STATUS_PAGE_UNAUTHORIZED_ACTIONS_DEFAULTS,
    body: 'Have questions or think you should be able to see what’s here? Please contact us and let us know so we can look into it!',
    footer: 'Think you should see something here or need help with this?',
    headline: 'Hmm...you don’t currently have access to view this content.',
  },
  language: 'en',
  override_color: false,
  override_logo: false,
  preferred_size: PREFER_SMALLER,
  trigger_position: TRIGGER_POSITION_RIGHT,
  trigger_shape: TRIGGER_SHAPE_PILL,
} as const);

// WARNING: Never modify fallback values
const FLOW_STATUS_PAGE_ACTIONS_FALLBACKS = deepFreeze([
  {
    label: 'Go to homepage',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_PRIMARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_CHURCH,
    value: 'homepage',
  },
  {
    label: '',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_SECONDARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_URL,
    value: '',
  },
  {
    label: '',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_TERTIARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_URL,
    value: '',
  },
] as const);

const FLOW_STATUS_PAGE_UNAUTHENTICATED_ACTIONS_FALLBACKS = deepFreeze([
  {
    label: '',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_PRIMARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_CHURCH,
    value: '',
  },
  {
    label: 'or, Go to homepage',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_SECONDARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_CHURCH,
    value: 'homepage',
  },
  {
    label: '',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_TERTIARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_URL,
    value: '',
  },
] as const);

const FLOW_STATUS_PAGE_UNAUTHORIZED_ACTIONS_FALLBACKS = deepFreeze([
  {
    label: 'Go to homepage',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_PRIMARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_CHURCH,
    value: 'homepage',
  },
  {
    label: '',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_SECONDARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_URL,
    value: '',
  },
  {
    label: 'Let us know!',
    priority: FLOW_STATUS_PAGE_ACTION_PRIORITY_TERTIARY,
    type: FLOW_STATUS_PAGE_ACTION_TYPE_CHURCH,
    value: 'email',
  },
] as const);

export const FLOW_FALLBACKS = deepFreeze({
  color_flow: STANDARD_COLOR_DEFAULT,
  color_mode: COLOR_MODE_AUTO,
  [FLOW_STATUS_PAGE_CLOSED]: {
    actions: FLOW_STATUS_PAGE_ACTIONS_FALLBACKS,
    body: 'You might have missed the deadline or we’ve reached capacity. Hopefully we’ll be able to get you in next time!',
    headline: 'Sorry! This Flow is now closed :(',
  },
  [FLOW_STATUS_PAGE_COMING_SOON]: {
    actions: FLOW_STATUS_PAGE_ACTIONS_FALLBACKS,
    body: 'It looks like you’re just a little bit too early to complete this particular Flow. But it looks like it will be available soon! In the meantime, feel free to visit our website for all other info.',
    headline: 'Almost, but not yet… This Flow is coming soon!',
  },
  [FLOW_STATUS_PAGE_UNAVAILABLE]: {
    actions: FLOW_STATUS_PAGE_ACTIONS_FALLBACKS,
    body: 'It’s possible you’ve found something that isn’t quite ready for you yet, or you’ve stumbled upon an old link that is no longer available. Either way, we hope you find what you’re looking for.',
    headline: 'Hmm… this Flow isn’t available right now.',
  },
  [FLOW_STATUS_PAGE_UNAUTHENTICATED]: {
    actions: FLOW_STATUS_PAGE_UNAUTHENTICATED_ACTIONS_FALLBACKS,
    body: 'You may also need to have a connection type set with your church (e.g. member, regular attender) depending on how privacy has been set up for this Flow.',
    headline: 'You must sign in to view this content.',
  },
  [FLOW_STATUS_PAGE_UNAUTHORIZED]: {
    actions: FLOW_STATUS_PAGE_UNAUTHORIZED_ACTIONS_FALLBACKS,
    body: 'Have questions or think you should be able to see what’s here? Please contact us and let us know so we can look into it!',
    footer: 'Think you should see something here or need help with this?',
    headline: 'Hmm...you don’t currently have access to view this content.',
  },
  language: 'en',
  override_color: false,
  override_logo: false,
  preferred_size: PREFER_SMALLER,
  trigger_position: TRIGGER_POSITION_RIGHT,
  trigger_shape: TRIGGER_SHAPE_PILL,
} as const);

export const FLOW_REVISION_DEFAULTS = deepFreeze({
  account_creation_mode: 'default',
  show_title: false,
  message_receipt_enabled: true,
  message_thankyou_title: 'Thank you!',
  message_thankyou_body:
    'We’re so blessed you’ve taken this next step with our church family. You are loved and we thank the Lord for you.',
  message_thankyou_type: 'default',
  message_thankyou_actions: [],
  message_thankyou_actions_type: 'default',
  message_thankyou_enabled_fields: { actions: true, body: true, footer: true, signIn: true, title: true },
  message_thankyou_external_url_title: 'Submission Received!',
  message_thankyou_external_url_body: `Redirecting you to the confirmation page…\n\nIf your browser does not redirect you automatically in a few seconds, you can click the link below:`,
  message_thankyou_external_url_open_in_new_tab: true,
  message_thankyou_sign_in_prompt_title: '👋 Hey there! Want to save this submission to your account? Just sign in.',
  message_thankyou_sign_in_prompt_body:
    '(You’ll also fly through Flows a lot faster in the future with your info pre-filled!)',
  message_thankyou_footer: [
    {
      type: 'paragraph',
      children: [{ text: 'Have a question or need help with anything? We’re here for you.' }],
    },
    {
      type: 'paragraph',
      children: [{ text: '' }],
    },
    {
      type: 'paragraph',
      children: [
        { text: '' },
        {
          type: 'parameter',
          parameter: 'churchEmailAddress',
          fallback: '',
          children: [{ text: 'Let us know!' }],
        },
        { text: '' },
      ],
    },
  ],
} satisfies Partial<FlowRevision>);

// WARNING: Never modify fallback values
export const FLOW_REVISION_FALLBACKS = deepFreeze({
  account_creation_mode: 'default',
  show_title: false,
  message_receipt_enabled: true,
  message_thankyou_title: 'Thank you!',
  message_thankyou_body: '',
  message_thankyou_type: 'default',
  message_thankyou_actions: [],
  message_thankyou_actions_type: 'default',
  message_thankyou_enabled_fields: { actions: true, body: true, footer: true, signIn: true, title: true },
  message_thankyou_external_url_title: 'Submission Received!',
  message_thankyou_external_url_body: `Redirecting you to the confirmation page…\n\nIf your browser does not redirect you automatically in a few seconds, you can click the link below:`,
  message_thankyou_external_url_open_in_new_tab: true,
  message_thankyou_sign_in_prompt_title: '👋 Hey there! Want to save this submission to your account? Just sign in.',
  message_thankyou_sign_in_prompt_body:
    '(You’ll also fly through Flows a lot faster in the future with your info pre-filled!)',
  message_thankyou_footer: [
    {
      type: 'paragraph',
      children: [{ text: 'Have a question or need help with anything? We’re here for you.' }],
    },
    {
      type: 'paragraph',
      children: [{ text: '' }],
    },
    {
      type: 'paragraph',
      children: [
        { text: '' },
        {
          type: 'parameter',
          parameter: 'churchEmailAddress',
          fallback: '',
          children: [{ text: 'Let us know!' }],
        },
        { text: '' },
      ],
    },
  ],
} satisfies Partial<FlowRevision>);

export const GIVINGFLOW_DEFAULTS = deepFreeze({
  name: 'GivingFlow',
  trigger_icon: TRIGGER_ICON_GIVE,
  trigger_shape: TRIGGER_SHAPE_CIRCLE,
} as const);

export const GIVINGFLOW_FALLBACKS = deepFreeze({
  name: 'GivingFlow',
  trigger_icon: TRIGGER_ICON_GIVE,
  trigger_shape: TRIGGER_SHAPE_CIRCLE,
} as const);

export const GIVINGFLOW_REVISION_DEFAULTS = deepFreeze({
  title: 'GivingFlow',
  message_thankyou_title: 'Thank You!',
  message_thankyou_body: 'We appreciate your generosity.',
  options: {
    memo: {
      enabled: false,
    },
    smart_contact: {
      enabled: false,
      steps: [
        {
          type: STEP_TYPE.SMART_NAME,
          config: {
            active: true,
          },
        },
        {
          type: STEP_TYPE.SMART_EMAIL_ADDRESS,
          config: {
            active: true,
          },
        },
        {
          type: STEP_TYPE.SMART_PHONE,
          config: {
            active: false,
          },
        },
        {
          type: STEP_TYPE.SMART_PHYSICAL_ADDRESS,
          config: {
            active: false,
          },
        },
      ],
    },
  },
} as const);

export const GIVINGFLOW_REVISION_FALLBACKS = deepFreeze({
  title: 'GivingFlow',
  message_thankyou_title: 'Thank You!',
  message_thankyou_body: 'We appreciate your generosity.',
  options: {
    memo: {
      enabled: false,
    },
    smart_contact: {
      enabled: false,
      steps: [
        {
          type: STEP_TYPE.SMART_NAME,
          config: {
            active: true,
          },
        },
        {
          type: STEP_TYPE.SMART_EMAIL_ADDRESS,
          config: {
            active: true,
          },
        },
        {
          type: STEP_TYPE.SMART_PHONE,
          config: {
            active: false,
          },
        },
        {
          type: STEP_TYPE.SMART_PHYSICAL_ADDRESS,
          config: {
            active: false,
          },
        },
      ],
    },
  },
} as const);

export const PRAYERFLOW_REVISION_DEFAULTS = deepFreeze({
  title: 'PrayerFlow',
  message_thankyou_body:
    "We're so blessed you've shared your prayer with our church family. You are loved and we thank the Lord for you.",
  message_thankyou_title: 'Thank You!',
} as const);

export const PRAYERFLOW_REVISION_FALLBACKS = PRAYERFLOW_REVISION_DEFAULTS;

export const ACCESS_REQUEST_FLOW_REVISION_DEFAULTS = deepFreeze({
  title: 'AccessRequestFlow',
  message_thankyou_title: 'Got it!',
  message_thankyou_body:
    'We try to review and approve these requests at least a couple times a week. Please note, we only approve requests from those in our church family, as this is a private prayer tool for us to share openly with one another.',
  message_receipt_body: `We review these requests as quickly as we can, but you may not get access same day, so please be patient. If we’re not your home church, you will most likely not get approved, as this is a private prayer wall for those in our church family. Thank you.`,
} as const);

export const ACCESS_REQUEST_FLOW_REVISION_FALLBACKS = ACCESS_REQUEST_FLOW_REVISION_DEFAULTS;
